"use client";

import { UserEvents, trackEvent } from "@/analytics";
import { useEffect, useState } from "react";

import { Comedian } from "@/data/comedians";
import Link from "next/link";
import LockedUnlockedOverlay from "./LockedUnlockedOverlay";
import SpecialCardImage from "./SpecialCardImage";
import SpecialPlaybackProgress from "./SpecialPlaybackProgress";
import SpecialTitleAndRuntime from "./SpecialTitleAndRuntime";
import { SpecialWithUnlocked } from "@/data/specials";
import { cn } from "@/components/common/utils";
import { getCDNImageURL } from "@/data/images";
import { useProductsForSpecial } from "@punchup/data/hooks";

export interface SpecialCardProps {
  comedian: Comedian;
  special: SpecialWithUnlocked;
  playbackProgress?: number;
  width: number;
  height: number;
  mobileWidth: number;
  mobileHeight: number;
  useSquareImgOnMobile: boolean;
  className?: string;
  mobileClassName?: string;
  location: string;
  hideLockOverlay?: boolean;
  customLink?: string;
}

export const SpecialCardShimmer: React.FC<{
  width?: number;
  mobileWidth?: number;
}> = ({ width = 420, mobileWidth = 340 }) => {
  return (
    <div className={`w-[${mobileWidth}px] lg:w-[${width}px]`}>
      <div className="overflow-hidden bg-gray-200 rounded-xl animate-pulse">
        <div className="h-[236px] object-cover hidden lg:block">
          <div className="h-[236px] object-cover lg:block" />
        </div>
        <div className="h-[236px] object-contain bg-grey lg:hidden" />
      </div>
      <div className="py-4 overflow-hidden">
        <div className="rounded-lg normal-case font-sans font-semibold bg-gray-200 animate-pulse h-7 lg:h-9 w-[12rem] lg:w-[24rem] mb-2 lg:mb-3" />
        <div className="flex items-center w-20 h-5 mb-4 text-sm text-white bg-gray-200 rounded-lg animate-pulse lg:w-32" />
      </div>
    </div>
  );
};

export default function SpecialCard({
  comedian,
  special,
  playbackProgress,
  width,
  height,
  mobileWidth,
  mobileHeight,
  useSquareImgOnMobile,
  className,
  mobileClassName,
  location,
  hideLockOverlay = false,
  customLink,
}: SpecialCardProps) {
  const {
    unlocked,
    slug,
    title,
    runtime,
    requires_subscription,
    requires_purchase,
    preview_img,
    small_preview_img,
    price,
    id,
    livestream,
  } = special;
  const [posterImg, setPosterImg] = useState<string | null>(null);
  const [mobilePosterImg, setMobilePosterImg] = useState<string | null>(null);
  const { products } = useProductsForSpecial(special);

  useEffect(() => {
    const getImgSrc = async () => {
      const posterImg = preview_img
        ? getCDNImageURL(
            preview_img,
            `gravity=auto,fit=crop,height=${height},width=${width}`
          )
        : null;
      setPosterImg(posterImg);
      const mobilePosterImg = small_preview_img
        ? getCDNImageURL(
            small_preview_img,
            `gravity=auto,fit=crop,height=${mobileHeight},width=${mobileWidth}`
          )
        : null;
      setMobilePosterImg(mobilePosterImg);
    };

    if (!mobilePosterImg && !posterImg) {
      getImgSrc();
    }
  }, [
    mobilePosterImg,
    posterImg,
    preview_img,
    small_preview_img,
    width,
    height,
    mobileWidth,
    mobileHeight,
  ]);

  if (!mobilePosterImg && !posterImg) return <SpecialCardShimmer />;

  const trackClickedWatchSpecial = () => {
    trackEvent(UserEvents.CLICKED_WATCH_SPECIAL, {
      SpecialId: id,
      ComedianId: comedian.id,
      SpecialPrice: price,
      source: location,
      unlocked,
    });
  };

  const handleUnlockedSpecialClick = () => {
    // always track the clicked watch special event
    trackClickedWatchSpecial();

    trackEvent(UserEvents.CLICKED_WATCH_UNLOCKED_SPECIAL, {
      SpecialId: id,
      ComedianId: comedian.id,
      SpecialPrice: price,
      location,
    });
  };

  const handleLockedSpecialClick = () => {
    // always track the clicked watch special event
    trackClickedWatchSpecial();

    trackEvent(UserEvents.CLICKED_UNLOCK_SPECIAL, {
      SpecialId: id,
      ComedianId: comedian.id,
      SpecialPrice: price,
      location,
    });
  };

  const handleLinkClick = () =>
    unlocked ? handleLockedSpecialClick : handleUnlockedSpecialClick;

  const cardHref = customLink
    ? customLink
    : unlocked
      ? `/${comedian.slug}/${slug}`
      : `/${comedian.slug}/${slug}/checkout`;

  return (
    <>
      {/* mobile version */}
      <Link
        className={cn("group md:hidden", mobileClassName)}
        href={cardHref}
        onClick={handleLinkClick}
      >
        <div className="relative flex flex-col items-center justify-center overflow-hidden rounded-xl">
          <SpecialCardImage
            width={mobileWidth}
            height={mobileHeight}
            comedianDisplayName={comedian.display_name}
            // render "mobile image" with narrow aspect ratio -OR- render the wide-angle desktop image
            imgSrc={useSquareImgOnMobile ? mobilePosterImg : posterImg}
            className={useSquareImgOnMobile ? "aspect-square" : "aspect-video"}
            unlocked={unlocked}
          />
          {!hideLockOverlay && (
            <LockedUnlockedOverlay
              unlocked={unlocked}
              product={products?.[0] ?? null}
              specialRequiresPurchase={requires_purchase}
              specialRequiresSubscription={requires_subscription}
              label={special.title}
            />
          )}
          <SpecialPlaybackProgress playbackProgress={playbackProgress} />
        </div>
        <div className="flex flex-col justify-center flex-1 w-full pt-3 pr-1 md:pt-4">
          <SpecialTitleAndRuntime
            title={title}
            runtime={runtime}
            isLivestream={livestream !== null}
          />
        </div>
      </Link>
      {/* desktop version */}
      <Link
        className={cn("group hidden md:block", className)}
        style={{ width }}
        href={cardHref}
        onClick={handleLinkClick}
      >
        <div className="relative flex flex-col items-center justify-center overflow-hidden rounded-xl">
          <SpecialCardImage
            width={width}
            height={height}
            className="aspect-video"
            comedianDisplayName={comedian.display_name}
            imgSrc={posterImg}
            unlocked={unlocked}
          />
          {!hideLockOverlay && (
            <LockedUnlockedOverlay
              unlocked={unlocked}
              product={products?.[0] ?? null}
              specialRequiresPurchase={requires_purchase}
              specialRequiresSubscription={requires_subscription}
              label={special.title}
            />
          )}
          <SpecialPlaybackProgress playbackProgress={playbackProgress} />
        </div>
        <div className="flex flex-col justify-center flex-1 w-full pt-3 pr-1 md:pt-4">
          <SpecialTitleAndRuntime
            title={title}
            runtime={runtime}
            isLivestream={livestream !== null}
          />
        </div>
      </Link>
    </>
  );
}
